/**
 * LandingNav Component
 *
 * Modern floating navigation bar for the landing page.
 * Rounded pill design with smooth entrance animations that match the hero.
 *
 * TODO: Refactor to use UI layer components per docs/ui-contract.md
 * This component has landing-page-specific styling that should be extracted
 * into reusable UI components (FloatingNav, NavPill, etc.)
 */

/* eslint-disable @innolek/no-tailwind-outside-ui */
/* eslint-disable @innolek/no-button-styling-override */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Menu, ShoppingCart, Sparkles, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Center, Container, Icon, Text } from '@/ui';
import type { FirebaseUser } from '@/features/auth';
import { AuthModal, authService } from '@/features/auth';
import { useCartItemCount, useOpenCartDrawer } from '@/features/cart';
import { LanguageSwitch } from '@/shared/components/LanguageSwitch';
import { CountrySelectorModal, CountryStatus } from '@/features/country';
import { logger } from '@/shared/lib/logger/logger';

// Static animation variants - extracted for performance
const NAV_VARIANTS = {
    hidden: { opacity: 0, y: -20, scale: 0.98 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] as const, delay: 0.1 },
    },
} as const;

const LOGO_HOVER = { scale: 1.08, rotate: 8 } as const;
const LOGO_TAP = { scale: 0.95 } as const;
const LOGO_TRANSITION = { type: 'spring', stiffness: 400, damping: 17 } as const;

const CART_BADGE_INITIAL = { scale: 0, opacity: 0 } as const;
const CART_BADGE_ANIMATE = { scale: 1, opacity: 1 } as const;
const CART_BADGE_EXIT = { scale: 0, opacity: 0 } as const;
const CART_BADGE_TRANSITION = { type: 'spring', stiffness: 500, damping: 25 } as const;

const ICON_CLOSE_INITIAL = { rotate: -90, opacity: 0 } as const;
const ICON_CLOSE_ANIMATE = { rotate: 0, opacity: 1 } as const;
const ICON_CLOSE_EXIT = { rotate: 90, opacity: 0 } as const;
const ICON_MENU_INITIAL = { rotate: 90, opacity: 0 } as const;
const ICON_MENU_ANIMATE = { rotate: 0, opacity: 1 } as const;
const ICON_MENU_EXIT = { rotate: -90, opacity: 0 } as const;
const ICON_TRANSITION = { duration: 0.15 } as const;

const CTA_HOVER = { scale: 1.02 } as const;
const CTA_TAP = { scale: 0.98 } as const;

const MOBILE_MENU_INITIAL = { opacity: 0, height: 0 } as const;
const MOBILE_MENU_ANIMATE = { opacity: 1, height: 'auto' } as const;
const MOBILE_MENU_EXIT = { opacity: 0, height: 0 } as const;
const MOBILE_MENU_TRANSITION = { duration: 0.3, ease: [0.16, 1, 0.3, 1] } as const;

const MOBILE_STAGGER_VARIANTS = {
    open: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
    closed: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
} as const;

const MOBILE_ITEM_VARIANTS = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -16 },
} as const;

const SCROLL_LISTENER_OPTIONS = { passive: true } as const;

export const LandingNav = React.memo(function LandingNav(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<FirebaseUser | null>(null);
    const cartItemCount = useCartItemCount();
    const openCartDrawer = useOpenCartDrawer();

    const handleMobileNavClick = useCallback((): void => {
        setIsMobileMenuOpen(false);
    }, []);

    useEffect(() => {
        const checkAuth = async (): Promise<void> => {
            try {
                const authenticated = await authService.isAuthenticated();
                setIsAuthenticated(authenticated);
                if (authenticated) {
                    const currentUser = authService.getCurrentUser();
                    setUser(currentUser);
                }
            } catch (error) {
                logger.error(
                    '[LandingNav] Error checking auth status',
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        component: 'LandingNav',
                        action: 'check_auth_status',
                    }
                );
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        void checkAuth();

        const unsubscribe = authService.onAuthStateChange((_event, session) => {
            const authUser = session?.user ?? null;
            setIsAuthenticated(!!authUser);
            setUser(authUser);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        const handleScroll = (): void => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll, SCROLL_LISTENER_OPTIONS);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleOpenAuthModal = useCallback((): void => {
        setIsAuthModalOpen(true);
    }, []);

    const handleCloseAuthModal = useCallback((): void => {
        setIsAuthModalOpen(false);
    }, []);

    const handleToggleMobileMenu = useCallback((): void => {
        setIsMobileMenuOpen(prev => !prev);
    }, []);

    const handleNavigateDashboard = useCallback((): void => {
        void navigate('/dashboard');
    }, [navigate]);

    const handleMobileAuthClick = useCallback((): void => {
        handleMobileNavClick();
        handleOpenAuthModal();
    }, [handleMobileNavClick, handleOpenAuthModal]);

    const handleMobileDashboardClick = useCallback((): void => {
        handleMobileNavClick();
        void navigate('/dashboard');
    }, [handleMobileNavClick, navigate]);

    const navLinks = useMemo(() => [
        { to: '/workshop', label: t('ui.nav.create', 'Create') },
        { to: '/about', label: t('ui.nav.about', 'About') },
    ], [t]);

    const mobileNavLinks = useMemo(() => [
        { to: '/workshop', label: t('ui.nav.create', 'Create'), icon: '✨' },
        { to: '/about', label: t('ui.nav.about', 'About'), icon: '💜' },
        { to: '/faq', label: t('ui.nav.faq', 'FAQ'), icon: '💡' },
        { to: '/contact', label: t('ui.nav.contact', 'Contact'), icon: '💌' },
    ], [t]);

    return (
        <>
            <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
                <Container size="6xl" padding="md" className="pt-4 lg:pt-5">
                    <motion.div
                        variants={NAV_VARIANTS}
                        initial="hidden"
                        animate="visible"
                    >
                        <div className={`flex items-center justify-between w-full px-4 lg:px-6 py-3 lg:py-4 rounded-full backdrop-blur-lg border transition-all duration-300 pointer-events-auto ${
                            isScrolled
                                ? 'bg-background/95 border-border shadow-lg'
                                : 'bg-background/80 border-border/50 shadow-md'
                        }`}>
                            {/* Logo */}
                            <Link
                                to="/"
                                className="flex items-center gap-2.5 lg:gap-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg flex-shrink-0"
                            >
                                <motion.div
                                    whileHover={LOGO_HOVER}
                                    whileTap={LOGO_TAP}
                                    transition={LOGO_TRANSITION}
                                >
                                    <img
                                        src="/favicon.ico"
                                        alt="Tangi"
                                        className="w-8 h-8 lg:w-10 lg:h-10 aspect-square"
                                    />
                                </motion.div>
                                <Text size="heading-4" weight="semibold" variant="default" style={{ letterSpacing: '-0.025em' }}>
                                    Tangi
                                </Text>
                            </Link>

                            {/* Desktop Navigation Links */}
                            <nav className="hidden lg:flex items-center gap-1 xl:gap-2 mx-8">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        className="px-3 xl:px-4 py-2 rounded-lg text-sm lg:text-base font-medium text-foreground/80 hover:text-foreground hover:bg-accent/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </nav>

                            {/* Right Actions */}
                            <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-shrink-0">
                                {/* Country - Desktop only, hidden on smaller screens */}
                                <div className="hidden xl:block">
                                    <CountryStatus />
                                </div>

                                {/* Language Switch */}
                                <LanguageSwitch variant="compact" />

                                {/* Cart */}
                                <div className="relative">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={openCartDrawer}
                                        aria-label={`Cart${cartItemCount > 0 ? ` (${cartItemCount} items)` : ''}`}
                                    >
                                        <Icon icon={ShoppingCart} size="md" />
                                    </Button>
                                    <AnimatePresence>
                                        {cartItemCount > 0 && (
                                            <motion.div
                                                initial={CART_BADGE_INITIAL}
                                                animate={CART_BADGE_ANIMATE}
                                                exit={CART_BADGE_EXIT}
                                                transition={CART_BADGE_TRANSITION}
                                                className="absolute -top-0.5 -right-0.5"
                                            >
                                                <Badge variant="default">
                                                    <Text size="xs" weight="bold">
                                                        {cartItemCount > 9 ? '9+' : cartItemCount}
                                                    </Text>
                                                </Badge>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Mobile Menu Toggle */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleToggleMobileMenu}
                                    aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                                    className="lg:hidden"
                                >
                                    <AnimatePresence mode="wait">
                                        {isMobileMenuOpen ? (
                                            <motion.div
                                                key="close"
                                                initial={ICON_CLOSE_INITIAL}
                                                animate={ICON_CLOSE_ANIMATE}
                                                exit={ICON_CLOSE_EXIT}
                                                transition={ICON_TRANSITION}
                                            >
                                                <Icon icon={X} size="xs" />
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="menu"
                                                initial={ICON_MENU_INITIAL}
                                                animate={ICON_MENU_ANIMATE}
                                                exit={ICON_MENU_EXIT}
                                                transition={ICON_TRANSITION}
                                            >
                                                <Icon icon={Menu} size="xs" />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Button>

                                {/* CTA Buttons - Desktop */}
                                <div className="hidden lg:flex items-center gap-2">
                                    {isLoading ? (
                                        <Center style={{ width: '7rem' }}>
                                            <Icon icon={Loader2} size="md" color="muted" style={{ animation: 'spin 1s linear infinite' }} />
                                        </Center>
                                    ) : isAuthenticated && user ? (
                                        <Button
                                            variant="ghost"
                                            onClick={handleNavigateDashboard}
                                        >
                                            {t('ui.nav.dashboard')}
                                        </Button>
                                    ) : (
                                        <>
                                            {/* LOGIN button */}
                                            <Button
                                                variant="ghost"
                                                onClick={handleOpenAuthModal}
                                            >
                                                {t('ui.nav.signIn')}
                                            </Button>
                                            {/* CREATE button */}
                                            <motion.div whileHover={CTA_HOVER} whileTap={CTA_TAP}>
                                                <Button
                                                    onClick={(): void => { void navigate('/workshop'); }}
                                                    variant="primary"
                                                >
                                                    <Icon icon={Sparkles} size="sm" />
                                                    {t('ui.nav.create')}
                                                </Button>
                                            </motion.div>
                                        </>
                                    )}
                                </div>
                            </div>

                        </div>
                    </motion.div>

                    {/* Mobile Menu - Outside nav but within animation wrapper */}
                    <AnimatePresence>
                        {isMobileMenuOpen && (
                            <motion.div
                                initial={MOBILE_MENU_INITIAL}
                                animate={MOBILE_MENU_ANIMATE}
                                exit={MOBILE_MENU_EXIT}
                                transition={MOBILE_MENU_TRANSITION}
                                className="lg:hidden overflow-hidden rounded-2xl liquid-glass shadow-liquid-glass border border-[hsl(var(--liquid-glass-border-medium))] mt-2 pointer-events-auto"
                            >
                                <motion.div
                                    initial="closed"
                                    animate="open"
                                    variants={MOBILE_STAGGER_VARIANTS}
                                    className="p-4 space-y-1"
                                >
                                    {mobileNavLinks.map((link) => (
                                        <motion.div
                                            key={link.to}
                                            variants={MOBILE_ITEM_VARIANTS}
                                        >
                                            <Link
                                                to={link.to}
                                                onClick={handleMobileNavClick}
                                                className="flex items-center gap-3 w-full px-4 py-3 text-lg font-medium text-foreground/90 hover:text-foreground hover:bg-accent/10 rounded-lg transition-colors"
                                            >
                                                <Text size="body-responsive">{link.icon}</Text>
                                                <Text size="base" weight="medium">{link.label}</Text>
                                            </Link>
                                        </motion.div>
                                    ))}

                                    {/* Mobile Country Selector */}
                                    <motion.div
                                        variants={MOBILE_ITEM_VARIANTS}
                                        className="pt-3 border-t border-border/30"
                                    >
                                        <div className="px-3 py-2">
                                            <CountryStatus />
                                        </div>
                                    </motion.div>

                                    {/* Mobile Auth */}
                                    <motion.div
                                        variants={MOBILE_ITEM_VARIANTS}
                                        className="pt-3 space-y-2"
                                    >
                                        {!isAuthenticated && !isLoading && (
                                            <>
                                                {/* LOGIN button */}
                                                <Button
                                                    onClick={handleMobileAuthClick}
                                                    fullWidth
                                                    variant="ghost"
                                                >
                                                    {t('ui.nav.signIn')}
                                                </Button>
                                                {/* CREATE button */}
                                                <Button
                                                    onClick={(): void => {
                                                        handleMobileNavClick();
                                                        void navigate('/workshop');
                                                    }}
                                                    fullWidth
                                                    variant="primary"
                                                >
                                                    <Icon icon={Sparkles} size="sm" style={{ marginRight: '0.5rem' }} />
                                                    {t('ui.nav.create')}
                                                </Button>
                                            </>
                                        )}
                                        {isAuthenticated && user && (
                                            <Button
                                                variant="secondary"
                                                onClick={handleMobileDashboardClick}
                                                fullWidth
                                            >
                                                {t('ui.nav.goToDashboard')}
                                            </Button>
                                        )}
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Container>
            </div>

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={handleCloseAuthModal}
                onSuccess={handleCloseAuthModal}
            />
            <CountrySelectorModal />
        </>
    );
});
